// src/utils/pdfGenerator.js
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // This imports the plugin but doesn't set it up correctly in some cases
import { format } from 'date-fns';

// Helper function to format currency
const formatCurrency = (amount) => {
  return `$${parseFloat(amount).toFixed(2)}`;
};

// Helper function to format dates
const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return format(date, 'EEEE, MMMM d, yyyy');
  } catch (e) {
    return dateString || 'Not scheduled';
  }
};

/**
 * Generate a PDF receipt for a booking
 * @param {Object} receiptData - The booking receipt data
 * @returns {jsPDF} - The PDF document object that can be saved or opened
 */
export const generateBookingReceipt = (receiptData) => {
  try {
    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set some variables for positioning
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    const col1 = margin;
    const col2 = pageWidth / 2;
    let y = margin;
    
    // Add logo (if available)
    // Uncomment and replace with your logo path when ready
    // doc.addImage('/logo.png', 'PNG', col1, y, 40, 15);
    
    // Add company information
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('JP Auto Detailing', col1, y + 10);
    y += 18;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('123 Main Street', col1, y);
    y += 5;
    doc.text('Aurora, IL 60506', col1, y);
    y += 5;
    doc.text('(630) 555-1234', col1, y);
    y += 5;
    doc.text('contact@jp-autodetailing.com', col1, y);
    y += 10;
    
    // Add receipt header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('BOOKING RECEIPT', col1, y);
    y += 10;
    
    // Add receipt info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Date and booking ID on the right
    doc.text(`Date: ${format(new Date(), 'MM/dd/yyyy')}`, col2, y - 10);
    if (receiptData.id) {
      doc.text(`Booking ID: ${receiptData.id.slice(-8)}`, col2, y - 5);
    }
    
    // Customer information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Information', col1, y);
    y += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (receiptData.customer) {
      doc.text(`Name: ${receiptData.customer.name || 'Not provided'}`, col1, y);
      y += 5;
      doc.text(`Email: ${receiptData.customer.email || 'Not provided'}`, col1, y);
      y += 10;
    } else {
      doc.text('Customer: Not provided', col1, y);
      y += 10;
    }
    
    // Service and appointment details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Service Details', col1, y);
    y += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Service
    if (receiptData.service) {
      doc.text(`Service: ${receiptData.service.title || 'Not specified'}`, col1, y);
      y += 5;
    }
    
    // Vehicle
    if (receiptData.vehicle) {
      doc.text(`Vehicle: ${receiptData.vehicle.year || ''} ${receiptData.vehicle.make || ''} ${receiptData.vehicle.model || ''}`.trim(), col1, y);
      y += 5;
    }
    
    // Appointment time
    if (receiptData.scheduling) {
      doc.text(`Appointment: ${formatDate(receiptData.scheduling.date)} at ${receiptData.scheduling.time || 'Not specified'}`, col1, y);
      y += 15;
    }
    
    // Payment details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Summary', col1, y);
    y += 10;
    
    // Create payment table manually since autoTable might not be working
    if (receiptData.payment) {
      // Draw table headers
      doc.setFillColor(59, 130, 246); // Blue color
      doc.setDrawColor(59, 130, 246);
      doc.setTextColor(255, 255, 255); // White text
      doc.rect(col1, y, pageWidth - (margin * 2), 8, 'F');
      
      doc.text('Description', col1 + 5, y + 5);
      doc.text('Amount', pageWidth - margin - 25, y + 5);
      y += 10;
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
      
      // Service row
      doc.text(receiptData.service?.title || 'Service', col1 + 5, y);
      doc.text(formatCurrency(receiptData.payment.subtotal), pageWidth - margin - 25, y);
      y += 8;
      
      // Draw light gray separator line
      doc.setDrawColor(200, 200, 200);
      doc.line(col1, y - 4, pageWidth - margin, y - 4);
      
      // Tax row
      doc.text('Tax', col1 + 5, y);
      doc.text(formatCurrency(receiptData.payment.tax), pageWidth - margin - 25, y);
      y += 8;
      
      // Draw separator line
      doc.setDrawColor(0, 0, 0);
      doc.line(col1, y - 4, pageWidth - margin, y - 4);
      
      // Total row
      doc.setFont('helvetica', 'bold');
      doc.text('Total', col1 + 5, y);
      doc.text(formatCurrency(receiptData.payment.total), pageWidth - margin - 25, y);
      
      y += 15;
      
      // Payment method
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text(`Paid with card ending in ${receiptData.payment.last4 || '****'}`, col1, y);
      y += 20;
    }
    
    // Thank you message
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Thank You For Your Business!', pageWidth / 2, y, { align: 'center' });
    y += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('We appreciate your trust in JP Auto Detailing.', pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.text('If you have any questions about your booking, please contact us.', pageWidth / 2, y, { align: 'center' });
    
    // Footer
    const footerY = doc.internal.pageSize.height - 10;
    doc.setFontSize(8);
    doc.text('www.jp-autodetailing.com', pageWidth / 2, footerY, { align: 'center' });
    
    return doc;
  } catch (error) {
    console.error('Error creating PDF document:', error);
    throw error;
  }
};

/**
 * Generate and download a PDF receipt
 * @param {Object} receiptData - The booking receipt data
 */
export const downloadBookingReceipt = (receiptData) => {
  try {
    const doc = generateBookingReceipt(receiptData);
    
    // Generate a filename based on booking ID or date
    const filename = receiptData.id 
      ? `receipt-${receiptData.id.slice(-8)}.pdf` 
      : `receipt-${new Date().getTime()}.pdf`;
    
    // Download the PDF
    doc.save(filename);
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return false;
  }
};

export default {
  generateBookingReceipt,
  downloadBookingReceipt
};